import React, { useState, useEffect, useRef } from 'react';
import { MoreVertical, Eye, Trash2, Edit, Share2 } from 'lucide-react';
import moment from 'moment';
import type { BlogItem } from 'features/DigitalMarketing/slice/types';
import { DMButton, DMDiv, DMImage, DMText } from './Primitives';

interface BlogCardProps {
  blog: BlogItem;
  isPublished?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onPreview?: () => void;
  onPublish?: () => void;
  onUnpublish?: () => void;
}

export const BlogCard = ({
  blog,
  isPublished = false,
  onEdit,
  onDelete,
  onPreview,
  onPublish,
  onUnpublish,
}: BlogCardProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const publishedValue =
    (blog as any).is_published ?? (blog as any).IS_PUBLISHED;
  const normalizedPublishedValue = String(publishedValue ?? '')
    .trim()
    .toLowerCase();
  const hasPublishedValue =
    publishedValue !== undefined &&
    publishedValue !== null &&
    normalizedPublishedValue !== '';
  const actualIsPublished =
    isPublished ||
    publishedValue === true ||
    (hasPublishedValue &&
      !['false', '0', 'no', 'unpublished', 'unpublish', 'draft'].includes(
        normalizedPublishedValue
      ));

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const blogTitle = blog.title || blog.TITLE || 'No title available';
  const blogImage =
    blog.image ||
    blog.cover_image_url ||
    blog.COVER_IMAGE_URL ||
    'https://placehold.co/600x400/E1DCFD/5E4DE0?text=No+Image';
  const formattedDate = blog.display_datetime
    ? moment(blog.display_datetime).format('DD/MM/YYYY')
    : blog.date || '';

  const formattedTime = blog.display_datetime
    ? moment(blog.display_datetime).format('hh:mm A')
    : blog.time || '';

  const viewCount = blog.view_count ?? blog.views ?? 0;

  return (
    <DMDiv className="group relative rounded-2xl border border-gray-200 bg-white transition-all hover:shadow-lg">
      <DMDiv
        className="aspect-[16/9] w-full overflow-hidden rounded-t-2xl bg-gray-100 cursor-pointer"
        onClick={onPreview}
      >
        <DMImage
          src={blogImage}
          alt={blogTitle}
          onError={e => {
            e.currentTarget.src =
              'https://placehold.co/600x400/E1DCFD/5E4DE0?text=No+Image';
          }}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </DMDiv>
      <DMDiv className="p-5">
        <DMDiv className="min-w-0 cursor-pointer" onClick={onPreview}>
          <DMText
            as="h3"
            className="mb-3 truncate text-lg font-bold leading-tight text-gray-900"
            title={blogTitle}
          >
            {blogTitle}
          </DMText>
        </DMDiv>
        <DMDiv className="flex items-center justify-between text-[10px] text-gray-500">
          <DMDiv
            className="flex items-center gap-1 cursor-pointer"
            onClick={onPreview}
          >
            <DMText as="span">{formattedDate}</DMText>
            <DMText as="span">|</DMText>
            <DMText as="span">{formattedTime}</DMText>
            <DMText as="span">|</DMText>
            <DMText as="span" className="flex items-center gap-1">
              <Eye size={9} /> {viewCount}
            </DMText>
          </DMDiv>

          <DMDiv className="relative" ref={menuRef}>
            <DMButton
              onClick={e => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="rounded-full p-1 hover:bg-gray-100"
            >
              <MoreVertical size={16} />
            </DMButton>

            {showMenu && (
              <DMDiv className="absolute bottom-full right-0 z-10 mb-1 w-32 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg">
                {actualIsPublished ? (
                  <DMButton
                    onClick={() => {
                      setShowMenu(false);
                      onUnpublish?.();
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Share2 size={14} className="rotate-180" /> Unpublish
                  </DMButton>
                ) : (
                  <DMButton
                    onClick={() => {
                      setShowMenu(false);
                      onPublish?.();
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Share2 size={14} /> Publish
                  </DMButton>
                )}
                <DMButton
                  onClick={() => {
                    setShowMenu(false);
                    if (!actualIsPublished) {
                      onEdit();
                    }
                  }}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-sm ${
                    actualIsPublished
                      ? 'cursor-not-allowed text-gray-400'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  disabled={actualIsPublished}
                >
                  <Edit size={14} /> Edit
                </DMButton>
                <DMButton
                  onClick={() => {
                    setShowMenu(false);
                    if (!actualIsPublished) {
                      onDelete();
                    }
                  }}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-sm ${
                    actualIsPublished
                      ? 'cursor-not-allowed text-gray-400'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                  disabled={actualIsPublished}
                >
                  <Trash2 size={14} /> Delete
                </DMButton>
              </DMDiv>
            )}
          </DMDiv>
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
};
